<template>
    <main
        class="main-layout"
        id="main-content"
        :class="{
            'sidebar-hidden': !sidebarExpanded,
            'sidebar-non-sticky': !sidebarSticky,
            'light-theme': currentTheme === 'light',
            'dark-theme': currentTheme === 'dark',
            'daltonic-red-green-theme': currentTheme === 'colorblind-red-green-theme',
            'daltonic-blue-yellow-theme': currentTheme === 'colorblind-blue-yellow-theme',
            'daltonic-tritanopia-theme': currentTheme === 'colorblind-tritanopia-theme',
            'daltonic-total-theme': currentTheme === 'colorblind-total-theme'
        }"
    >
        <a href="#main-content" @click="skipMainContent" class="skip-link">
            <span>{{ $t("Skip to main content") }}</span>
        </a>
        <TopBar
            @toggle-menu="toggleMenu"
            @toggle-notifications-menu="toggleNotificationsMenu"
            @openModal="openModal"
            :unreadCount="unreadCount"
        ></TopBar>
        <MenuSideBar v-model:expanded="sidebarExpanded"></MenuSideBar>
        <div v-if="sidebarExpanded" class="side-bar-overlay" @click="closeSideBar"></div>
        <AuthLoadingOverlay :display="loadingAuth"></AuthLoadingOverlay>

        <div v-if="$route.path === '/notifications'">
            <router-view v-if="!loadingAuth" @updateUnreadCount="unreadCount = $event" @updateNotifications="updateNotifications" />
        </div>
        <div v-else>
            <router-view v-if="!loadingAuth" />
        </div>

        <LoadingOverlay v-if="loadingRoute"></LoadingOverlay>

        <AccountSettingsDropdown
            v-if="displayDropdownAccountSettings"
            v-model:display="displayDropdownAccountSettings"
            @openModal="openModal"
        ></AccountSettingsDropdown>

        <NotificationsDropdown
            v-if="displayDropdownNotifications"
            v-model:display="displayDropdownNotifications"
            :notifications="events"
            @updateNotifications="events = $event"
            @updateUnreadCount="unreadCount = $event"
        />

        <ChangeLanguageDropdown
            v-if="displayDropdownLanguage"
            v-model:display="displayDropdownLanguage"
            @openModal="openModal"
        ></ChangeLanguageDropdown>

        <ChangeLanguageModal v-if="displayModalLanguage" v-model:display="displayModalLanguage"></ChangeLanguageModal>

        <ChangeThemeDropdown
            v-if="displayDropdownTheme"
            v-model:display="displayDropdownTheme"
            @openModal="openModal"
        ></ChangeThemeDropdown>

        <SelectWalletDropdown
            v-if="displayDropdownSelectWallet"
            v-model:display="displayDropdownSelectWallet"
            @openModal="openModal"
        ></SelectWalletDropdown>

        <ProfileModal v-if="displayProfileModal" v-model:display="displayProfileModal" :uid="profileModalUser"></ProfileModal>

        <LogoutModal v-if="displayModalLogout" v-model:display="displayModalLogout"></LogoutModal>

        <MessageModal
            v-if="displayModalMessage"
            v-model:display="displayModalMessage"
            :title="messageModalTitle"
            :message="messageModalMessage"
            :isError="messageModalIsError"
        ></MessageModal>

        <ConfirmationModal
            v-if="displayConfirmationModal"
            v-model:display="displayConfirmationModal"
            :title="confirmationTitle"
            :message="confirmationMessage"
            :danger="confirmationDanger"
            @confirm="onConfirm"
        >
        </ConfirmationModal>

        <CookiesModal v-if="displayModalCookies" v-model:display="displayModalCookies"></CookiesModal>

        <SnackBar></SnackBar>
        <NotificationModal :visible="showModal" :message="message" @mark-as-read="markAlarmAsRead" @close="showModal = false" />
    </main>
</template>

<script lang="ts">
import { defineComponent } from "vue";

import TopBar from "@/components/layout/TopBar.vue";
import MenuSideBar from "@/components/layout/MenuSideBar.vue";
import NotificationModal from "@/components/modals/NotificationModal.vue";
import LoadingOverlay from "@/components/layout/LoadingOverlay.vue";
import AuthLoadingOverlay from "@/components/layout/AuthLoadingOverlay.vue";
import SnackBar from "@/components/layout/SnackBar.vue";

import LogoutModal from "@/components/modals/auth/LogoutModal.vue";

import AccountSettingsDropdown from "@/components/dropdowns/account/AccountSettingsDropdown.vue";

import NotificationsDropdown from "@/components/dropdowns/account/NotificationsDropdown.vue";

import ChangeLanguageDropdown from "@/components/dropdowns/account/ChangeLanguageDropdown.vue";
import ChangeThemeDropdown from "@/components/dropdowns/account/ChangeThemeDropdown.vue";

import SelectWalletDropdown from "@/components/dropdowns/wallet/SelectWalletDropdown.vue";

import ChangeLanguageModal from "@/components/modals/ChangeLanguageModal.vue";

import ProfileModal from "@/components/modals/ProfileModal.vue";

import MessageModal from "@/components/modals/MessageModal.vue";

import ConfirmationModal from "@/components/modals/ConfirmationModal.vue";

import CookiesModal from "@/components/modals/CookiesModal.vue";

import { AuthController } from "@/control/auth";
import { getCookiePreference, getTheme } from "@/control/app-preferences";
import { getEventPollingService, destroyEventPollingService } from "@/utils/event-polling";
import { getMQTTEventClient, destroyMQTTEventClient } from "@/utils/mqtt-client";
import { getTaskScheduler } from "@/utils/task-scheduler";
import { ApiEvents } from "@/api/api-events";
import { Request } from "@asanrom/request-browser";
import { setNotifyState } from "@/utils/azureOpenAi/STS/TTS/textToSpeech";

export default defineComponent({
    components: {
        TopBar,
        MenuSideBar,
        LoadingOverlay,
        AuthLoadingOverlay,
        SnackBar,
        LogoutModal,
        AccountSettingsDropdown,
        ChangeLanguageDropdown,
        ChangeThemeDropdown,
        MessageModal,
        SelectWalletDropdown,
        ChangeLanguageModal,
        ConfirmationModal,
        ProfileModal,
        CookiesModal,
        NotificationModal,
        NotificationsDropdown,
    },
    name: "MainLayout",
    setup: function () {
        return {
            confirmationCallback: null,
            resizeObserver: null as ResizeObserver,
        };
    },
    data: function () {
        return {
            unreadCount: 0,
            events: [],

            loadingRoute: false,

            sidebarExpanded: window.innerWidth >= 1000,
            sidebarSticky: true,

            isDarkTheme: false,
            currentTheme: getTheme(), 
            loadingAuth: AuthController.Loading && !AuthController.FirstTimeLoaded,

            displayModalLogout: false,
            displayDropdownAccountSettings: false,

            displayDropdownNotifications: false,

            displayDropdownTheme: false,

            displayDropdownLanguage: false,

            displayDropdownSelectWallet: false,

            displayModalLanguage: false,

            displayModalMessage: false,
            messageModalTitle: "",
            messageModalMessage: "",
            messageModalIsError: false,

            displayConfirmationModal: false,
            confirmationTitle: "",
            confirmationMessage: "",
            confirmationDanger: false,

            displayProfileModal: false,
            profileModalUser: "",

            displayModalCookies: false,

            showModal: false,
            message: "",
            eventID: "",
        };
    },
    computed: {
        unreadNotifications() {
            return this.events.filter((n) => !n.read).length;
        },
    },
    methods: {
        updateNotifications(updatedNotifications) {
            this.events = updatedNotifications;
        },
        markAlarmAsRead: function () {
            Request.Pending("mark-alarm-read-modal", ApiEvents.MarkEventAsRead(this.eventID))
                .onSuccess(() => {
                    this.showModal = false;
                })
                .onRequestError((err, handleErr) => {
                    handleErr(err, {
                        unauthorized: () => {
                            this.$router.push("/login");
                        },
                        notFound: () => {
                            console.error(`${this.$t("Notification not found")}`);
                        },
                        notFoundNotificationNotFound: () => {
                            console.error(`${this.$t("Notification not found")}`);
                        },
                        temporalError: () => {
                            console.error(`${this.$t("Temporal error marking notification as read")}`);
                        },
                    });
                })
                .onUnexpectedError(() => {
                    console.error(`${this.$t("Error marking notification as read")}`);
                });
        },
        onAuthStatusUpdate: function () {
            this.displayDropdownAccountSettings = false;

            this.displayModalLogout = false;

            this.displayDropdownLanguage = false;
            this.displayModalLanguage = false;

            this.displayDropdownTheme = false;

            this.displayDropdownSelectWallet = false;

            // Maneja el polling de notificaciones basado en el estado de autenticacion
            if (AuthController.isAuthenticated()) {
                this.startNotificationPolling();
            } else {
                this.stopNotificationPolling();
            }
        },

        closeSideBar: function () {
            this.sidebarExpanded = false;
        },

        startNotificationPolling: function () {
            // Configurar TaskScheduler para eventos programados
            const taskScheduler = getTaskScheduler();
            if (!taskScheduler.getStatus()) {
                taskScheduler.initialize({
                    onNotificationExecute: (event) => {
                        if (event.type === "timer" || event.type === "alarm") {
                            this.eventID = event.id;
                            this.message = event.title || event.description || "Timer/Alarm notification";
                            this.showModal = true;
                        }

                        // Mostrar notificación programada en la UI
                        this.showScheduledNotification(event);
                    },
                    onScheduleUpdate: (_tasks) => {
                        // Handle schedule updates
                    },
                    onError: (error, context) => {
                        console.error("[MainLayout] TaskScheduler error:", error, "context:", context);
                    },
                });
            }

            // Configurar el sistema híbrido: MQTT + Polling como fallback
            const pollingService = getEventPollingService({
                intervalMs: 60000, // Polling cada minuto
                maxEvents: 10, // Limite para el dropdown
                eventType: "notification", // Solo notificaciones
                lang: localStorage.getItem("app-pref-lang"),
                onEventsUpdate: (events) => {
                    this.events = events;
                },
                onUnreadCountUpdate: (count) => {
                    this.unreadCount = count;
                },
                onError: (_error) => {},
                onUnauthorized: () => {
                    this.$router.push("/login");
                },
            });

            // Configurar cliente MQTT
            const mqttClient = getMQTTEventClient({
                onEventReceived: (event) => {
                    // Si el evento tiene la marca 'scheduled: true', significa que es un evento programado ejecutándose
                    // y debe mostrarse inmediatamente en la UI, no enviarse al TaskScheduler
                    if ((event as any).scheduled === true) {
                        // Agregar el evento ejecutado al inicio de la lista
                        this.events = [event, ...this.events].slice(0, 10);
                        // Actualizar contador de no leídos
                        this.unreadCount = this.events.filter((e) => !e.read).length;
                    } else if (this.hasSchedule(event)) {
                        taskScheduler.scheduleEvent(event, Date.now());
                    } else {
                        // Agregar el nuevo evento al inicio de la lista
                        this.events = [event, ...this.events].slice(0, 10); // Mantener solo últimos 10
                        // Actualizar contador de no leídos
                        this.unreadCount = this.events.filter((e) => !e.read).length;
                    }
                },
                onConnectionChange: (connected) => {
                    if (connected) {
                        // MQTT conectado - hacer fetch inicial y detener polling
                        pollingService.pollNow();
                        pollingService.setMQTTMode(true); // Detiene completamente el polling
                    } else {
                        if (!pollingService.isActive()) {
                            pollingService.startPolling(); // Inicia polling si no está activo
                        }
                        pollingService.setMQTTMode(false); // Activa el polling
                    }
                },
                onError: (_error) => {
                    if (!pollingService.isActive()) {
                        pollingService.startPolling(); // Inicia polling si no está activo
                    }
                    pollingService.setMQTTMode(false); // Activa el polling
                },
            });

            // Intenta MQTT primero, se usa polling solo como fallback
            mqttClient.connect();

            // Solo iniciar polling si MQTT falla después de 5 segundos
            setTimeout(() => {
                if (!mqttClient.isConnectedToMQTT()) {
                    pollingService.startPolling();
                }
            }, 5000);
        },

        stopNotificationPolling: function () {
            destroyEventPollingService();
            destroyMQTTEventClient();
            // Limpiar TaskScheduler al detener notificaciones
            const taskScheduler = getTaskScheduler();
            if (!taskScheduler.getStatus()) {
                taskScheduler.reset();
            }
            this.events = [];
            this.unreadCount = 0;
        },

        /**
         * Verifica si un evento tiene configuración de schedule
         */
        hasSchedule: function (event) {
            const schedule = event.schedule;
            return (
                !!(
                    schedule &&
                    (schedule.cronExpression || schedule.startDate || schedule.endDate || (schedule.day && schedule.hour !== undefined))
                ) || !!event.cronExpression
            );
        },

        /**
         * Muestra una notificación programada ejecutada por TaskScheduler
         */
        showScheduledNotification: function (event) {
            // Llamar al backend para ejecutar el evento programado (esto lo publica a MQTT)
            Request.Pending(
                "execute-scheduled-event",
                ApiEvents.ExecuteScheduledEvent(event.id)
            )
            .onSuccess(() => {
                // El evento aparecerá automáticamente via MQTT por lo que no es necesario actualizar la UI aquí
            })
            .onRequestError((err, handleErr) => {
                handleErr(err, {
                    unauthorized: () => {
                        // Como fallback, agregar localmente
                        this.events = [event, ...this.events].slice(0, 10);
                        this.unreadCount = this.events.filter(e => !e.read).length;
                    },
                    temporalError: () => {
                        // Como fallback, agregar localmente
                        this.events = [event, ...this.events].slice(0, 10);
                        this.unreadCount = this.events.filter(e => !e.read).length;
                    },
                    badRequest: () => {
                        // Como fallback, agregar localmente
                        this.events = [event, ...this.events].slice(0, 10);
                        this.unreadCount = this.events.filter(e => !e.read).length;
                    },
                });
            })      
        },

        onAuthLoadingChanged: function () {
            this.loadingAuth = AuthController.Loading && !AuthController.FirstTimeLoaded;
        },

        onThemeChanged: function () {
            this.currentTheme = getTheme();
        },

        onLanguageChanged: function () {
            // Update polling service language when language changes
            if (AuthController.isAuthenticated()) {
                const pollingService = getEventPollingService();
                const newLang = localStorage.getItem("app-pref-lang") || "en";

                pollingService.updateConfig({
                    lang: newLang,
                });
                // Trigger immediate polling to get updated translations
                pollingService.pollNow();

                // Translate existing events in-place without affecting programmed notifications
                this.translateExistingEvents();
            }
        },

        translateExistingEvents: function () {
            const lang = localStorage.getItem("app-pref-lang") || "en";

            Request.Pending("translate-existing-events", ApiEvents.GetUserEvents(0, 10, lang, "notification"))
                .onSuccess((freshEvents) => {
                    if (Array.isArray(freshEvents) && Array.isArray(this.events)) {
                        this.events = this.events.map((existingEvent) => {
                            const freshEvent = freshEvents.find((fresh) => fresh.id === existingEvent.id);
                            if (freshEvent) {
                                return {
                                    ...existingEvent,
                                    title: freshEvent.title,
                                    description: freshEvent.description,
                                };
                            }
                            return existingEvent;
                        });
                    }
                })
                .onRequestError(() => {})
                .onUnexpectedError(() => {});
        },

        toggleMenu: function () {
            this.sidebarExpanded = !this.sidebarExpanded;
        },
        toggleNotificationsMenu: function () {
            this.displayDropdownNotifications = !this.displayDropdownNotifications;
        },

        skipMainContent: function (event) {
            if (event) {
                event.preventDefault();
            }
            const content: any = document.querySelector(".page-content");
            if (content) {
                content.focus();
            }
        },

        showMessage: function (title: string, msg: string, isError: boolean = false) {
            this.messageModalTitle = title;
            this.messageModalMessage = msg;
            this.messageModalIsError = isError;
            this.displayModalMessage = true;
        },

        openModal: function (name: string) {
            switch (name) {
                case "account-settings":
                    this.displayDropdownAccountSettings = !this.displayDropdownAccountSettings;
                    break;
                case "change-language":
                    this.displayDropdownLanguage = true;
                    break;
                case "change-language-modal":
                    this.displayModalLanguage = true;
                    break;
                case "change-theme":
                    this.displayDropdownTheme = true;
                    break;
                case "logout":
                    this.displayModalLogout = true;
                    break;
                case "select-wallet":
                    this.displayDropdownSelectWallet = true;
                    break;
            }
        },

        onRouterLoading: function (l: boolean) {
            this.loadingRoute = l;
        },

        onRouteChanged: function () {
            if (this.$route && this.$route.meta && typeof this.$route.meta === "object") {
                this.sidebarSticky = !!this.$route.meta.sidebarSticky;
                if (this.sidebarSticky) {
                    this.sidebarExpanded = window.innerWidth >= 1000;
                } else {
                    this.sidebarExpanded = false;
                }
            } else {
                this.sidebarExpanded = false;
                this.sidebarSticky = false;
            }

            const cookiePref = getCookiePreference();

            if (!cookiePref && (!this.$route || !(this.$route.name in { terms: 1, privacy: 1, cookies: 1 }))) {
                this.displayModalCookies = true;
            } else {
                this.displayModalCookies = false;
            }
        },

        showConfirmationModal: function (options: { title: string; message: string; danger?: boolean; callback: () => void }) {
            this.displayConfirmationModal = true;
            this.confirmationTitle = options.title;
            this.confirmationMessage = options.message;
            this.confirmationDanger = !!options.danger;
            this.confirmationCallback = options.callback;
        },

        onConfirm: function () {
            if (this.confirmationCallback) {
                this.confirmationCallback();
            }
        },

        onShowProfile: function (uid: string) {
            this.profileModalUser = uid;
            this.displayProfileModal = true;
        },

        onPageResize: function () {
            if (this.$route.name !== "not-found") {
                this.sidebarExpanded = window.innerWidth >= 1000;
            }
        },
    },
    mounted: function () {
        // Inicia el polling de notificaciones si esta autenticado
        if (AuthController.isAuthenticated()) {
            this.startNotificationPolling();
        }
        this.$listenOnAppEvent("auth-status-changed", this.onAuthStatusUpdate.bind(this));

        this.$listenOnAppEvent("auth-status-loading", this.onAuthLoadingChanged.bind(this));

        this.$listenOnAppEvent("theme-changed", this.onThemeChanged.bind(this));
        this.onThemeChanged();

        this.$listenOnAppEvent("set-locale", this.onLanguageChanged.bind(this));

        this.$listenOnAppEvent("msg-modal", this.showMessage.bind(this));
        this.$listenOnAppEvent("ask-confirmation", this.showConfirmationModal.bind(this));
        this.$listenOnAppEvent("show-profile", this.onShowProfile.bind(this));

        this.$listenOnAppEvent("router-load-state-change", this.onRouterLoading.bind(this));

        this.onAuthStatusUpdate();

        this.onRouteChanged();

        this.unreadCount = this.unreadNotifications;

        if (window.ResizeObserver) {
            this.resizeObserver = new ResizeObserver(this.onPageResize.bind(this));
            this.resizeObserver.observe(this.$el);
        }
    },
    watch: {
        $route: function () {
            this.onRouteChanged();
        },
        notifications: function () {
            this.unreadCount = this.unreadNotifications;
        },
        unreadCount: function (newCount) {
            setNotifyState(newCount > 0);
        },
    },
    beforeUnmount: function () {
        // Limpia el polling de notificaciones
        this.stopNotificationPolling();
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    },
});
</script>

<style>
.skip-link {
  position: absolute;
  top: -40px;
  left: 0;
  background: #005fcc;
  color: white;
  padding: 8px 16px;
  z-index: 1000;
  text-decoration: none;
  border-radius: 4px;
  font-weight: bold;
}

.skip-link:focus {
  top: 10px;
  left: 10px;
}
</style>
